import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { IPhone } from './interface'
import { Button } from 'antd'
import { IconsWrapper } from './styles'
import { modalPhone } from './mango-call'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import ru from 'react-phone-input-2/lang/ru.json'
import { ContactIcon, PhoneIcon, TelegramIcon, WhatsappIcon } from '../../assets/icons'

const normalizePhone = (phone?: string | null): string => {
  if (typeof phone !== 'string') {
    return ''
  }
  return phone.replace(/[^\d]/g, '')
}

const formatWithPlus = (digits: string): string => (digits ? `+${digits}` : '')

function InputPhone({ value, onChange, UISchema }: IPhone) {
  const disabled = useMemo(() => Boolean(UISchema?.options?.disabled), [UISchema])
  const areaCodes = [
    '310',
    '311',
    '312',
    '313',
    '315',
    '318',
    '321',
    '324',
    '325',
    '326',
    '327',
    '336',
    '7172',
    '73622',
  ]
  const areaCodesWithBrackets = areaCodes.map(code => `(${code})`)
  const hideDashes = useMemo(() => Boolean(UISchema?.options?.hideDashes), [UISchema])
  const customer_id = useMemo(() => UISchema?.options?.customer_id, [UISchema])
  const [localValue, setLocalValue] = useState<string>(() => normalizePhone(value))

  useEffect(() => {
    const normalized = normalizePhone(value)
    setLocalValue(prev => (prev === normalized ? prev : normalized))
  }, [value])

  const getFormatLength = useCallback((country: any) => {
    const format = country?.['format']
    if (!format) {
      return null
    }
    return (format.match(/\./g) || []).length
  }, [])

  const handleValueChange = useCallback(
    (nextValue: string, country: any) => {
      const digits = nextValue.replace(/[^\d]/g, '')
      setLocalValue(digits)

      const expectedLength = getFormatLength(country)

      if (digits.length === 0) {
        if (value) {
          onChange?.('')
        }
        return
      }

      if (expectedLength && digits.length === expectedLength) {
        const formatted = formatWithPlus(digits)
        if (formatted !== value) {
          onChange?.(formatted)
        }
      }
    },
    [getFormatLength, onChange, value],
  )

  const onCall = useCallback(() => {
    const digits = normalizePhone(value)
    if (!digits) {
      console.warn('Некорректный номер телефона для звонка')
      return
    }
    modalPhone({ phone: formatWithPlus(digits) })
  }, [value])

  const onClient = useCallback(async () => {
    Object.assign(window.location, { href: `/client-new/${customer_id}` })
  }, [customer_id])

  return (
    <div
      style={{ display: 'flex', width: 'max-content', gap: '2px' }}
      className="input_phone_number"
    >
      {!hideDashes && customer_id && (
        <Button
          type="link"
          onClick={onClient}
          style={{ padding: '0', display: 'flex' }}
          disabled={false}
          icon={<ContactIcon />}
        />
      )}
      <PhoneInput
        enableSearch={true}
        searchPlaceholder={'Поиск'}
        localization={ru}
        country={'ru'}
        onlyCountries={['ru', 'kz', 'by', 'tj', 'uz', 'am', 'kg', 'az', 'md', 'tm']}
        value={localValue}
        disabled={disabled}
        containerStyle={{ width: 'auto' }}
        inputStyle={{ borderRadius: '6px', height: '32px' }}
        areaCodes={areaCodesWithBrackets}
        masks={{ kz: '(...) ..-..-..' }}
        isValid={(number, country) => {
          if (number.length < 3) {
            return true
          }
          const expectedLength = getFormatLength(country)
          if (!expectedLength) {
            return true
          }
          return number.length <= expectedLength
        }}
        onChange={handleValueChange}
      />
      <IconsWrapper>
        <Button
          type="link"
          href={`https://t.me/+${value?.replace(/\D/g, '')}`}
          target="_blank"
          style={{ padding: '0', display: 'flex', overflow: 'hidden', border: 'none' }}
          disabled={!Boolean(value)}
          icon={<TelegramIcon disabled={!Boolean(value)} />}
        />
        <Button
          type="link"
          href={`https://wa.me/+${value?.replace(/\D/g, '')}`}
          target="_blank"
          style={{ padding: '0', display: 'flex', overflow: 'hidden', border: 'none' }}
          disabled={!Boolean(value)}
          icon={<WhatsappIcon disabled={!Boolean(value)} />}
        />
        <Button
          type="link"
          onClick={onCall}
          style={{ padding: '0', display: 'flex', overflow: 'hidden', border: 'none' }}
          disabled={!Boolean(value)}
          icon={<PhoneIcon disabled={!Boolean(value)} />}
        />
      </IconsWrapper>
    </div>
  )
}

export default InputPhone
